import type { CategoryKind } from './category';
import type { CategoryId, ProjectId } from './ids';
import { CURRENT_SCHEMA_VERSION, type Project } from './project';
import { fixedRate, type AbsoluteRateSpec } from './rate';
import { constant } from './scheduled';
import type { YearMonth } from './time';

export const DEFAULT_CATEGORIES = [
  ['salary', 'income'],
  ['otherIncome', 'income'],
  ['housing', 'expense'],
  ['food', 'expense'],
  ['transport', 'expense'],
  ['utilities', 'expense'],
  ['leisure', 'expense'],
  ['debt', 'expense'],
  ['taxes', 'expense'],
  ['otherExpense', 'expense'],
] as const satisfies readonly (readonly [string, CategoryKind])[];

export type DefaultCategoryKey = (typeof DEFAULT_CATEGORIES)[number][0];

export const DEFAULT_HORIZON_MONTHS = 40 * 12;
export const DEFAULT_CPI = 0.025;
/**
 * CPI for new projects from the app's templates: the same 2.5 % mean (deterministic runs are
 * unchanged), with ±1 pp yearly volatility and persistence so Monte Carlo shows a real range.
 */
export const DEFAULT_UNCERTAIN_CPI: AbsoluteRateSpec = {
  kind: 'distribution',
  mean: DEFAULT_CPI,
  volatility: 0.01,
  autocorrelation: 0.5,
};
export const DEFAULT_MC_PATHS = 5000;

export interface NewProjectArgs {
  id: ProjectId;
  name: string;
  start: YearMonth;
  currency: string;
  seed: number;
  /** Localised names for the default categories. */
  categoryNames: Record<DefaultCategoryKey, string>;
}

export function defaultCategoryId(key: DefaultCategoryKey): CategoryId {
  return `cat-${key}` as CategoryId;
}

/** A new, empty project with sensible global assumptions and default categories. */
export function createProject(args: NewProjectArgs): Project {
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: args.id,
    name: args.name,
    assumptions: {
      start: args.start,
      horizonMonths: DEFAULT_HORIZON_MONTHS,
      startingCash: 0,
      currency: args.currency,
      cpi: constant(fixedRate(DEFAULT_CPI)),
      seed: args.seed >>> 0,
      mcPaths: DEFAULT_MC_PATHS,
    },
    categories: DEFAULT_CATEGORIES.map(([key, kind]) => ({
      id: defaultCategoryId(key),
      name: args.categoryNames[key],
      kind,
    })),
    items: {},
    scenarios: [],
    baselineScenarioId: null,
    goals: [],
    investments: [],
    keyDates: [],
  };
}
